import { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Paperclip, Trash2 } from 'lucide-react';
import { toast } from 'react-toastify';
import CompanyHeader from '@/components/company/CompanyHeader';
import { SecondaryButton, SubmitButton } from '@/components/ui/Button';
import { Skeleton } from '@/components/ui/skeleton';
import useTenantState from '@/states/tenant.state';
import { getMyKpiDetail, updateMyKpiRealization, uploadKpiEvidence, deleteKpiEvidence } from '@/api/myKpi';
import { IMyKpi } from '@/states/myKpi.state';
import { errorHandler } from '@/utils/functions/errorHandler';
import {
  IMedia,
  NameMap,
  StatusBadge,
  InfoRow,
  formatDate,
  formatFileSize,
  downloadKpiMedia,
  resolveNameMap,
  ApprovalStatusBanner,
  ApprovedKpiView,
} from '@/pages/company-profile/kpi/kpiShared';

interface IMyKpiItem {
  id: number;
  org_key_activity_id: number;
  org_job_description_id: number;
  weight: number;
  target: number;
  measurement?: string;
  actual_value: number | null;
  manager_actual?: number | null;
  aob?: number | null;
  score?: number | null;
  notes: string;
  evidence_url: string;
  media: IMedia[];
}

interface IMyKpiDetailData extends Omit<IMyKpi, 'id' | 'start_date' | 'end_date' | 'score'> {
  id: number;
  kpi_template_id?: number;
  user_id?: number;
  start_date?: string;
  end_date?: string;
  items: IMyKpiItem[];
  template?: { id: number; name: string; period_status?: string };
  score?: number | null;
  category?: string | null;
  notes_employee?: string;
  notes_manager?: string;
  period_status?: string;
}

interface EditableItem {
  id: number;
  actual_value: string;
  notes: string;
  media: IMedia[];
  uploading: boolean;
}

const EDITABLE_STATUSES = ['DRAFT', 'REJECTED', 'REVISION'];

function MyKpiDetailSkeleton() {
  return (
    <div className="px-5 space-y-6">
      <div>
        <Skeleton className="h-5 w-24 mb-3" />
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Skeleton className="h-4 w-40 flex-shrink-0" />
            <Skeleton className="h-4 w-48" />
          </div>
          <div className="flex items-center gap-2">
            <Skeleton className="h-4 w-40 flex-shrink-0" />
            <Skeleton className="h-4 w-36" />
          </div>
          <div className="flex items-center gap-2">
            <Skeleton className="h-4 w-40 flex-shrink-0" />
            <Skeleton className="h-5 w-20 rounded-full" />
          </div>
        </div>
      </div>

      {[1, 2].map(i => (
        <div key={i} className="rounded-xl border border-gray-200 p-5 space-y-4">
          <Skeleton className="h-5 w-20" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2">
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Skeleton className="h-4 w-32 flex-shrink-0" />
                <Skeleton className="h-4 w-36" />
              </div>
              <div className="flex items-center gap-2">
                <Skeleton className="h-4 w-32 flex-shrink-0" />
                <Skeleton className="h-4 w-40" />
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Skeleton className="h-4 w-28 flex-shrink-0" />
                <Skeleton className="h-4 w-16" />
              </div>
              <div className="flex items-center gap-2">
                <Skeleton className="h-4 w-28 flex-shrink-0" />
                <Skeleton className="h-4 w-20" />
              </div>
            </div>
          </div>
          <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Skeleton className="h-4 w-28 mb-2" />
                <Skeleton className="h-10 w-full" />
              </div>
              <div>
                <Skeleton className="h-4 w-20 mb-2" />
                <Skeleton className="h-16 w-full" />
              </div>
            </div>
            <div>
              <Skeleton className="h-4 w-24 mb-2" />
              <Skeleton className="h-16 w-full rounded-lg" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

export default function MyKpiDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentTenant } = useTenantState();
  const fileInputRefs = useRef<Record<number, HTMLInputElement | null>>({});

  const [data, setData] = useState<IMyKpiDetailData | null>(null);
  const [loading, setLoading] = useState(true);
  const [nameMap, setNameMap] = useState<NameMap>({ keyActivities: {}, jobDescs: {} });
  const [editableItems, setEditableItems] = useState<EditableItem[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const tenantSlug = currentTenant?.slug ?? currentTenant?.id;
  const rawStatus = data?.status;
  const periodStatus = data?.period_status ?? data?.template?.period_status;
  const isPeriodActive = periodStatus === 'ACTIVE';
  const isInactive = String(rawStatus) === 'false';
  const status = typeof rawStatus === 'string' ? rawStatus.toUpperCase() : '';
  const isApproved = status === 'APPROVED';
  const isEditable = isInactive || !isPeriodActive ? false : EDITABLE_STATUSES.includes(status);

  useEffect(() => {
    const fetchDetail = async () => {
      if (!currentTenant?.id || !id) return;
      try {
        const response = await getMyKpiDetail(currentTenant.id, id);
        const raw = response?.data ?? response;
        setData(raw);

        if (Array.isArray(raw.items)) {
          setEditableItems(
            raw.items.map((item: IMyKpiItem) => ({
              id: item.id,
              actual_value:
                item.actual_value !== null && item.actual_value !== undefined ? String(item.actual_value) : '',
              notes: item.notes ?? '',
              media: item.media ?? [],
              uploading: false,
            })),
          );

          const resolved = await resolveNameMap(currentTenant.id, raw.items);
          setNameMap(resolved);
        }
      } catch {
        // silent fail; user sees empty state
      } finally {
        setLoading(false);
      }
    };
    fetchDetail();
  }, [currentTenant?.id, id]);

  const handleFileUpload = async (itemId: number, files: FileList | null) => {
    if (!files || files.length === 0) return;
    const file = files[0];

    const allowedExts = ['pdf', 'jpg', 'jpeg', 'png'];
    const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
    if (!allowedExts.includes(ext)) {
      toast.error('Format file tidak didukung. Gunakan PDF, JPG, JPEG, atau PNG.');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Ukuran file maksimal 10 MB.');
      return;
    }

    setEditableItems(prev => prev.map(ei => (ei.id === itemId ? { ...ei, uploading: true } : ei)));
    try {
      const result = await uploadKpiEvidence(itemId, file);
      const newMedia: IMedia = result?.data ?? result;
      setEditableItems(prev =>
        prev.map(ei => (ei.id === itemId ? { ...ei, media: [...ei.media, newMedia], uploading: false } : ei)),
      );
      toast.success('File berhasil diunggah');
    } catch (error) {
      setEditableItems(prev => prev.map(ei => (ei.id === itemId ? { ...ei, uploading: false } : ei)));
      errorHandler(error);
    }
    if (fileInputRefs.current[itemId]) {
      fileInputRefs.current[itemId]!.value = '';
    }
  };

  const handleDeleteMedia = async (itemId: number, mediaId: number) => {
    try {
      await deleteKpiEvidence(mediaId);
      setEditableItems(prev =>
        prev.map(ei => (ei.id === itemId ? { ...ei, media: ei.media.filter(m => m.id !== mediaId) } : ei)),
      );
      toast.success('File berhasil dihapus');
    } catch (error) {
      errorHandler(error);
    }
  };

  const buildPayload = (status?: string) => {
    const items = editableItems.map(ei => ({
      id: ei.id,
      actual_value: ei.actual_value !== '' ? Number(ei.actual_value) : null,
      notes: ei.notes,
      evidence_url: '',
    }));
    return status ? { status, items } : { items };
  };

  const handleSaveDraft = async () => {
    if (!currentTenant?.id || !id) return;
    setIsSaving(true);
    try {
      await updateMyKpiRealization(currentTenant.id, id, buildPayload());
      toast.success('Draft berhasil disimpan');
    } catch (error) {
      errorHandler(error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSubmit = async () => {
    if (!currentTenant?.id || !id) return;

    const missing = editableItems.filter(ei => ei.actual_value === '');
    if (missing.length > 0) {
      toast.error('Semua item KPI harus memiliki nilai realisasi sebelum submit.');
      return;
    }

    setIsSubmitting(true);
    try {
      await updateMyKpiRealization(currentTenant.id, id, buildPayload('SUBMITTED'));
      toast.success('KPI berhasil disubmit');
      const response = await getMyKpiDetail(currentTenant.id, id);
      const raw = response?.data ?? response;
      setData(raw);
    } catch (error) {
      errorHandler(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const templateName = data?.template_name ?? data?.template?.name ?? '-';

  return (
    <section className="w-full">
      <CompanyHeader
        title={
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => navigate(`/${tenantSlug}/kpi-saya`)}
              className="p-1.5 hover:bg-gray-100 rounded-md transition-colors"
              title="Kembali ke KPI Saya"
            >
              <ArrowLeft size={20} />
            </button>
            <span>{loading ? 'Detail KPI' : `Detail KPI - ${templateName}`}</span>
          </div>
        }
      />

      <div className="mx-5 border-b border-gray-200 mb-5" />

      {loading ? (
        <MyKpiDetailSkeleton />
      ) : !data ? (
        <div className="px-5">
          <p className="text-gray-400 text-sm">Data tidak tersedia</p>
        </div>
      ) : (
        <div className="px-5 space-y-6">
          {/* Status banner for APPROVED */}
          {isApproved && <ApprovalStatusBanner status={data.status} />}

          <div>
            <h2 className="text-base font-semibold text-gray-800 mb-3">Detail</h2>
            <div className="space-y-2">
              <InfoRow label="Nama Template" value={templateName} />
              <InfoRow
                label="Periode"
                value={
                  data.start_date && data.end_date
                    ? `${formatDate(data.start_date)} - ${formatDate(data.end_date)}`
                    : data.period ?? '-'
                }
              />
              <InfoRow label="Status" value={<StatusBadge status={data?.status} />} />
              {isApproved && data.progress !== null && data.progress !== undefined && (
                <InfoRow label="Progress" value={`${data.progress}%`} />
              )}
            </div>
          </div>

          {/* APPROVED: shared read-only table view */}
          {isApproved ? (
            <ApprovedKpiView
              items={Array.isArray(data.items) ? data.items : []}
              nameMap={nameMap}
              totalScore={data.score}
              category={data.category}
              notesManager={data.notes_manager}
            />
          ) : (
            <>
              {Array.isArray(data.items) && data.items.length > 0 && (
                <div>
                  <h2 className="text-base font-semibold text-gray-800 mb-3">Daftar KPI</h2>
                  <div className="space-y-4">
                    {data.items.map((item, idx) => {
                      const ei = editableItems.find(e => e.id === item.id);

                      return (
                        <div key={item.id ?? idx} className="rounded-xl border border-gray-200 p-5 space-y-4">
                          <h3 className="font-semibold text-gray-800">KPI #{idx + 1}</h3>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2">
                            <div className="space-y-2">
                              <div className="flex text-sm">
                                <span className="text-gray-500 w-32 flex-shrink-0">Aktivitas Utama</span>
                                <span className="text-gray-800">
                                  : {nameMap.keyActivities[item.org_key_activity_id] || '-'}
                                </span>
                              </div>
                              <div className="flex text-sm">
                                <span className="text-gray-500 w-32 flex-shrink-0">Uraian Tugas</span>
                                <span className="text-gray-800">
                                  : {nameMap.jobDescs[item.org_job_description_id] || '-'}
                                </span>
                              </div>
                            </div>
                            <div className="space-y-2">
                              <div className="flex text-sm">
                                <span className="text-gray-500 w-28 flex-shrink-0">Bobot</span>
                                <span className="text-gray-800">
                                  : {item.weight !== undefined ? `${item.weight}` : '-'}
                                </span>
                              </div>
                              <div className="flex text-sm">
                                <span className="text-gray-500 w-28 flex-shrink-0">Target KPI</span>
                                <span className="text-gray-800">: {item.target ?? '-'}</span>
                              </div>
                            </div>
                          </div>

                          {item.measurement && (
                            <div className="text-sm">
                              <span className="text-gray-500 font-medium block mb-1">Pengukuran</span>
                              <div
                                className="text-gray-800 prose prose-sm max-w-none"
                                dangerouslySetInnerHTML={{ __html: item.measurement }}
                              />
                            </div>
                          )}

                          <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <label className="block text-sm text-gray-600 mb-1">Realisasi KPI</label>
                                {isEditable ? (
                                  <input
                                    type="number"
                                    min={0}
                                    placeholder="Masukkan nilai realisasi..."
                                    value={ei?.actual_value ?? ''}
                                    onChange={e =>
                                      setEditableItems(prev =>
                                        prev.map(x => (x.id === item.id ? { ...x, actual_value: e.target.value } : x)),
                                      )
                                    }
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  />
                                ) : (
                                  <p className="text-sm text-gray-800">
                                    {item.actual_value !== null && item.actual_value !== undefined
                                      ? item.actual_value
                                      : '-'}
                                  </p>
                                )}
                              </div>
                              <div>
                                <label className="block text-sm text-gray-600 mb-1">Catatan</label>
                                {isEditable ? (
                                  <textarea
                                    rows={3}
                                    placeholder="Tambahkan catatan..."
                                    value={ei?.notes ?? ''}
                                    onChange={e =>
                                      setEditableItems(prev =>
                                        prev.map(x => (x.id === item.id ? { ...x, notes: e.target.value } : x)),
                                      )
                                    }
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                                  />
                                ) : (
                                  <p className="text-sm text-gray-800">{item.notes || '-'}</p>
                                )}
                              </div>
                            </div>

                            <div>
                              <label className="block text-sm text-gray-600 mb-2">Upload Bukti</label>

                              {ei && ei.media.length > 0 && (
                                <div className="space-y-2 mb-3">
                                  {ei.media.map(media => (
                                    <div
                                      key={media.id}
                                      className="flex items-center justify-between px-3 py-2 bg-white rounded-lg border border-gray-200"
                                    >
                                      <div className="flex items-center gap-2 min-w-0">
                                        <Paperclip className="w-4 h-4 text-gray-400 flex-shrink-0" />
                                        <span className="text-sm text-gray-700 truncate">
                                          {media.name || media.file_name}
                                        </span>
                                        <span className="text-xs text-gray-400 flex-shrink-0">
                                          {formatFileSize(media.size)}
                                        </span>
                                      </div>
                                      <div className="flex items-center gap-1 flex-shrink-0 ml-2">
                                        <button
                                          type="button"
                                          title="Unduh file"
                                          onClick={() => downloadKpiMedia(media.id, media.file_name || media.name)}
                                          className="p-1 text-blue-500 hover:bg-blue-50 rounded-md transition-colors"
                                        >
                                          <span className="sr-only">Unduh</span>
                                          <svg
                                            className="w-4 h-4"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth={2}
                                          >
                                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                            <polyline points="7 10 12 15 17 10" />
                                            <line x1="12" y1="15" x2="12" y2="3" />
                                          </svg>
                                        </button>
                                        {isEditable && (
                                          <button
                                            type="button"
                                            title="Hapus file"
                                            onClick={() => handleDeleteMedia(item.id, media.id)}
                                            className="p-1 text-red-500 hover:bg-red-50 rounded-md transition-colors"
                                          >
                                            <Trash2 className="w-4 h-4" />
                                          </button>
                                        )}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}

                              {isEditable ? (
                                <>
                                  <input
                                    ref={el => {
                                      fileInputRefs.current[item.id] = el;
                                    }}
                                    type="file"
                                    accept=".pdf,.jpg,.jpeg,.png"
                                    title="Unggah bukti dokumen KPI"
                                    aria-label="Unggah bukti dokumen KPI"
                                    className="hidden"
                                    onChange={e => handleFileUpload(item.id, e.target.files)}
                                  />
                                  <button
                                    type="button"
                                    onClick={() => fileInputRefs.current[item.id]?.click()}
                                    disabled={ei?.uploading}
                                    className="w-full py-5 border-2 border-dashed border-gray-300 rounded-lg text-sm text-gray-400 hover:border-blue-400 hover:text-blue-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                  >
                                    {ei?.uploading ? 'Mengunggah...' : 'Klik untuk mengunggah dokumen KPI'}
                                  </button>
                                  <p className="text-xs text-gray-400 mt-1">
                                    Format: PDF, JPG, JPEG, PNG. Maks 10 MB per file.
                                  </p>
                                </>
                              ) : (
                                (!ei || ei.media.length === 0) && (
                                  <p className="text-sm text-gray-400">Tidak ada file</p>
                                )
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {data.notes_manager && (
                <div>
                  <label htmlFor="notes-manager-readonly" className="block text-sm font-medium text-gray-700 mb-1">
                    Catatan Manajer
                  </label>
                  <textarea
                    id="notes-manager-readonly"
                    rows={3}
                    readOnly
                    value={data.notes_manager}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-gray-50 text-gray-600 resize-none cursor-default"
                  />
                </div>
              )}

              {isEditable && (
                <div className="flex gap-2 pt-2">
                  <SecondaryButton
                    type="button"
                    onClick={handleSaveDraft}
                    loading={isSaving}
                    disabled={isSaving || isSubmitting}
                  >
                    Simpan Draft
                  </SecondaryButton>
                  <SubmitButton
                    type="button"
                    onClick={handleSubmit}
                    loading={isSubmitting}
                    disabled={isSaving || isSubmitting}
                  >
                    Submit
                  </SubmitButton>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </section>
  );
}
